import { FC, useState, useEffect } from 'react';
import { useTranslation } from '@frontend/i18n';
import {
  Modal,
  useFormField,
  EmojiButton,
  TextField,
  ModalControlModalProps,
  Text,
  Button,
  useAlert,
  ContentLoader,
} from '@frontend/design-system';
import { useTeamChatStore } from '../../providers';
import {
  preDefinedStatusButtonStyle,
  textFieldStyles,
  preDefinedEmojiTextStyles,
  forYouTextStyle,
  emojiButtonStyle,
  emojiStartAdornmentStyle,
  predefinedEmojiStyle,
} from './user-status-settings-styles';

interface UserStatusSettingsProps {
  modalProps: ModalControlModalProps;
  closeModal: () => void;
}

const MODAL_WIDTH = 500;

export const UserStatusSettings: FC<UserStatusSettingsProps> = ({ modalProps, closeModal }) => {
  const { t } = useTranslation('team-chat');
  const { streamClient, currentUser } = useTeamChatStore(['streamClient', 'currentUser']);
  const [statusText, setStatusText] = useState<string>(currentUser?.status?.status?.text ?? '');
  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(currentUser?.status?.status?.emoji ?? null);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const statusTextField = useFormField({ type: 'text', value: statusText });
  const { error } = useAlert();

  const preDefinedStatus = [
    { emoji: '🗓️', text: t('In an Appointment') },
    { emoji: '🚙', text: t('Commuting') },
    { emoji: '🤒', text: t('Out sick') },
    { emoji: '🌴', text: t('Vacationing') },
  ];

  const onSave = async () => {
    try {
      if (streamClient && currentUser) {
        setShowLoader(true);
        await streamClient?.upsertUser({
          id: currentUser.userID,
          name: `${currentUser?.firstName} ${currentUser?.lastName}`,
          userStatus: {
            statusText: !!statusText ? statusText : null,
            statusDuration: null,
            statusExpiration: null,
            emoji: selectedEmoji,
          },
          // @ts-ignore weavePresence object does not exist on stream's user Response type
          ...(streamClient?.user?.weavePresence && { weavePresence: streamClient.user.weavePresence }),
        });
        closeModal();
        setShowLoader(false);
      }
    } catch {
      setShowLoader(false);
      error('Unable to set status at the moment, please try again later');
    }
  };

  const clearProgress = () => {
    setStatusText('');
    setSelectedEmoji(null);
  };

  useEffect(() => {
    setStatusText(currentUser?.status?.status?.text ?? '');
    setSelectedEmoji(currentUser?.status?.status?.emoji ?? null);
  }, [modalProps.show]);

  return (
    <Modal minWidth={MODAL_WIDTH} {...modalProps} onClose={() => closeModal()}>
      <Modal.Header onClose={closeModal}>{t('Set Your Status')}</Modal.Header>
      <Modal.Body css={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TextField
          label=''
          startAdornment={
            selectedEmoji ? (
              <Text role='img' size='large' css={emojiStartAdornmentStyle}>
                {selectedEmoji}
              </Text>
            ) : (
              <EmojiButton onSelect={({ emoji }) => setSelectedEmoji(emoji)} css={emojiButtonStyle} />
            )
          }
          name=''
          placeholder={t(`What's your status?`)}
          {...statusTextField}
          css={textFieldStyles}
          clearable={!!statusText}
          // Adding a onChange and value to the text field as we need to control the state of text field,
          // but the text field is not controlled with textFeild.value and textFeild.onChange.
          // Hence adding a onChange and value to the text field
          value={statusText}
          onChange={(e) => {
            // @ts-ignore Field change event comes as a prop, but there is nothing defined hence adding a ts-ignore
            if (e && e.target) {
              // @ts-ignore Field change event comes as a prop, but there is nothing defined hence adding a ts-ignore
              setStatusText(e.target.value);
            }
          }}
          endAdornment={
            !!(statusText || selectedEmoji) && (
              <Button variant='tertiary' onClick={clearProgress}>
                {t('Clear')}
              </Button>
            )
          }
        />
        <Text size='large' weight='bold' css={forYouTextStyle}>
          {t('For you')}
        </Text>
        {preDefinedStatus.map(({ emoji, text }) => (
          <Button
            key={`${emoji}-${text}`}
            variant='secondary'
            css={preDefinedStatusButtonStyle}
            onClick={() => {
              setStatusText(text);
              setSelectedEmoji(emoji);
            }}
          >
            <Text size='large' css={predefinedEmojiStyle}>
              {emoji}
            </Text>
            <Text css={preDefinedEmojiTextStyles}>{text}</Text>
          </Button>
        ))}
      </Modal.Body>
      <Modal.Actions
        disablePrimary={
          !!statusText || !!selectedEmoji
            ? false
            : currentUser?.status.status.emoji || currentUser?.status.status.text
            ? false
            : true
        }
        onPrimaryClick={onSave}
        onSecondaryClick={closeModal}
        primaryLabel={t('Save')}
        secondaryLabel={t('cancel')}
      />
      <ContentLoader show={showLoader} />
    </Modal>
  );
};
